/**
 * Secure Storage Utility for Frontend
 * Provides structured storage with expiration support
 * Note: JWT tokens are self-contained and signed, so no additional encryption needed
 */

import { logger } from './logger';

interface StorageData {
  value: string;
  timestamp: number;
  expiresIn?: number;
}

/**
 * Storage keys constants
 * Note: Tokens (access & refresh) are stored in HttpOnly cookies by backend
 */
export const STORAGE_KEYS = {
  USER_DATA: 'stx_user_data',
  USER_PERMISSIONS: 'stx_user_permissions',
} as const;

export class SecureStorage {
  /**
   * Set item in storage with optional expiration
   * Simplified: No encoding, direct storage for easier debugging
   */
  static setItem(key: string, value: string, expiresInMs?: number): void {
    try {
      const storageData: StorageData = {
        value,
        timestamp: Date.now(),
        expiresIn: expiresInMs,
      };

      // Store as plain JSON for easier debugging
      localStorage.setItem(key, JSON.stringify(storageData));
    } catch (error) {
      logger.error('Failed to set storage item:', error as Error);
    }
  }

  /**
   * Get item from storage
   * Returns null if expired or not found
   */
  static getItem(key: string): string | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }

      const storageData: StorageData = JSON.parse(stored);
      return storageData.value;
    } catch (error) {
      logger.error('Failed to get storage item:', error as Error);
      // If corrupted, clear it
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Remove item from storage
   */
  static removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error('Failed to remove storage item:', error as Error);
    }
  }

  /**
   * Clear all storage (only keys related to app)
   */
  static clear(): void {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      logger.error('Failed to clear storage:', error as Error);
    }
  }

  /**
   * Check if item exists and is not expired
   */
  static hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  }
}
